/**
 * SSO handoff — mint a short-lived custom token for a sibling *.aayatanveda.in
 * surface so a signed-in portal user lands there already authenticated.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  Phase 0 (2026-05-21, Anil) — re-implementation of the login/launcher
 *  hotfix (tag `login-launcher-hotfix-2026-05-20`, commit e55f02c).
 *
 *  The pre-rebuild portal carried this logic inline in `lib/auth.tsx`
 *  (`mintHandoffUrl`). The 2026-05-21 rebuild rewrote auth.tsx from scratch
 *  on the OAuth claims model and dropped the handoff helper, the launcher
 *  and the `?return=` flow entirely. The SERVER side survived intact:
 *
 *    - `mintHandoffToken` (avbp-functions/avbp_auth_v2.js) is deployed and
 *      live. It checks `request.auth.token.isTeam === true` and that
 *      `token.surfaces` includes the requested `targetSurface`, then returns
 *      a 60s custom token under `{ token }`.
 *    - `resolveTeamClaimsOnSignIn` (avbp_auth_oauth.js) — already called by
 *      the rebuilt auth.tsx on every sign-in — mints `isTeam` + `surfaces[]`
 *      into the custom claims (identical shape to the old PIN path).
 *
 *  So the handoff CHAIN is fully supported by the backend; only this thin
 *  client helper had to be re-created. Pure module — no React — so it stays
 *  unit-testable and importable from both Login and AppShell.
 * ─────────────────────────────────────────────────────────────────────────
 */
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from './firebase';

/** Surfaces the SSO handoff recognises. Mirrors VALID_SURFACES in
 *  avbp-functions/avbp_auth_v2.js. */
export type Surface = 'avbp' | 'hr' | 'capture';

/** `?return=` origins we will hand a signed-in user back to. Any URL whose
 *  origin is NOT in this map is ignored (open-redirect guard). */
const ALLOWED_RETURN_ORIGINS: Record<string, Surface> = {
  'https://portal.aayatanveda.in': 'avbp',
  'https://hr.aayatanveda.in': 'hr',
  'https://capture.aayatanveda.in': 'capture',
};

export interface ReturnTarget {
  /** The verbatim return URL (already origin-validated). */
  url: string;
  /** Which surface that origin maps to. */
  surface: Surface;
}

/**
 * PURE — validate a raw `?return=` value. Returns the matched target only if
 * the URL parses AND its origin is one of the three recognised
 * *.aayatanveda.in surfaces. Anything else → null (never redirect off-list).
 */
export function parseReturnTarget(
  returnUrl: string | null | undefined,
): ReturnTarget | null {
  if (!returnUrl) return null;
  try {
    const u = new URL(returnUrl);
    const origin = `${u.protocol}//${u.host}`;
    const surface = ALLOWED_RETURN_ORIGINS[origin];
    if (!surface) return null;
    return { url: returnUrl, surface };
  } catch {
    return null;
  }
}

/**
 * PURE — append `?legacy=1` to a destination URL so it shows its OWN login
 * instead of bouncing back to the portal. This is the guaranteed
 * never-stranded fallback: any aayatanveda.in surface treats `?legacy=1` as
 * "skip SSO, render the local sign-in screen".
 */
export function withLegacy(url: string): string {
  try {
    const u = new URL(url);
    u.searchParams.set('legacy', '1');
    return u.toString();
  } catch {
    return url;
  }
}

/**
 * Mint a 60s SSO handoff token for `targetSurface` and return the absolute
 * destination URL with the token in the URL fragment (`#token=…`) so the
 * token never reaches a server log.
 *
 * Throws if the user is not signed in, lacks the surface in their claims, or
 * the callable rejects — the CALLER is responsible for the `?legacy=1`
 * fallback on any throw (so the user is never stranded).
 */
export async function mintHandoffUrl(
  targetSurface: Surface,
  baseUrl: string,
): Promise<string> {
  const current = auth.currentUser;
  if (!current) throw new Error('Not signed in.');

  // Force a fresh ID token so the freshly-minted team claims
  // (resolveTeamClaimsOnSignIn runs on sign-in) are definitely on the token
  // the callable sees — closes a race the old M7 hotfix #3 also closed.
  try {
    await current.getIdToken(true);
  } catch (err) {
    console.warn('[handoff] ID-token refresh failed (continuing):', err);
  }

  const call = httpsCallable<{ targetSurface: Surface }, { token: string }>(
    functions,
    'mintHandoffToken',
  );
  const res = await call({ targetSurface });
  const token = res.data?.token;
  if (!token) throw new Error('Handoff token was empty.');

  // Token in the fragment (#) — never sent to the destination's server.
  const sep = baseUrl.includes('#') ? '&' : '#';
  return `${baseUrl}${sep}token=${encodeURIComponent(token)}`;
}
